import React, { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import { googleLogout } from '@react-oauth/google';
import { api, setAuthToken, getAuthToken, setOnUnauthorized } from '../services/api';
import { isTokenExpired, getTimeUntilExpiry, parseJwt } from '../utils/jwt';
import { silentRefreshToken } from '../utils/googleSilentAuth';

interface AuthInfo {
  email: string;
  name?: string;
  allowed: boolean;
}

interface AuthContextType {
  auth: AuthInfo | null;
  loading: boolean;
  error: string | null;
  login: (credential: string) => Promise<void>;
  logout: () => void;
  /** Whether a token refresh is needed soon */
  needsRefresh: boolean;
}

const AuthContext = createContext<AuthContextType>({
  auth: null,
  loading: true,
  error: null,
  login: async () => {},
  logout: () => {},
  needsRefresh: false,
});

export const useAuth = () => useContext(AuthContext);

const TOKEN_STORAGE_KEY = 'homeledger_auth_token';
// Check token every minute
const TOKEN_CHECK_INTERVAL = 60 * 1000;
// Consider token expired 5 minutes before actual expiry
const EXPIRY_BUFFER = 5 * 60 * 1000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [auth, setAuth] = useState<AuthInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [needsRefresh, setNeedsRefresh] = useState(false);
  const refreshTimerRef = useRef<number | null>(null);
  const refreshingRef = useRef(false);

  /** Validate a token by calling /api/me */
  const validateToken = useCallback(async (token: string): Promise<AuthInfo | null> => {
    setAuthToken(token);
    try {
      const data = await api.get<AuthInfo>('/api/me');
      return data;
    } catch {
      setAuthToken(null);
      return null;
    }
  }, []);

  /** Logout – clear token and auth state */
  const logout = useCallback(() => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    setAuthToken(null);
    setAuth(null);
    setError(null);
    setNeedsRefresh(false);
    googleLogout();
  }, []);

  /** Login with a Google credential (ID token) */
  const login = useCallback(async (credential: string) => {
    setLoading(true);
    setError(null);
    setNeedsRefresh(false);
    
    // Check token expiry before even validating
    if (isTokenExpired(credential, 0)) {
      setError('Token on vanhentunut. Yritä kirjautua uudelleen.');
      setLoading(false);
      return;
    }
    
    const authInfo = await validateToken(credential);
    if (authInfo) {
      localStorage.setItem(TOKEN_STORAGE_KEY, credential);
      setAuth(authInfo);
      
      // Log token expiry for debugging
      const timeLeft = getTimeUntilExpiry(credential);
      console.log(`Token valid for ${Math.round(timeLeft / 60000)} minutes`);
    } else {
      setError('Kirjautuminen epäonnistui');
    }
    setLoading(false);
  }, [validateToken]);

  /** Attempt silent refresh, logout only if it fails */
  const attemptSilentRefresh = useCallback(async (): Promise<boolean> => {
    if (refreshingRef.current) return false;
    refreshingRef.current = true;
    console.log('Attempting silent token refresh...');
    try {
      const newCredential = await silentRefreshToken();
      if (newCredential) {
        const authInfo = await validateToken(newCredential);
        if (authInfo) {
          localStorage.setItem(TOKEN_STORAGE_KEY, newCredential);
          setAuth(authInfo);
          setNeedsRefresh(false);
          setError(null);
          const timeLeft = getTimeUntilExpiry(newCredential);
          console.log(`Silent refresh OK, token valid for ${Math.round(timeLeft / 60000)} min`);
          return true;
        }
      }
    } catch (e) {
      console.log('Silent refresh failed:', e);
    } finally {
      refreshingRef.current = false;
    }
    return false;
  }, [validateToken]);

  // Set up global 401 handler
  useEffect(() => {
    setOnUnauthorized(async () => {
      console.log('Received 401, attempting silent refresh');
      const refreshed = await attemptSilentRefresh();
      if (!refreshed) {
        logout();
      }
    });
    return () => setOnUnauthorized(null);
  }, [logout, attemptSilentRefresh]);

  // Token expiry checking — attempt silent refresh instead of immediate logout
  useEffect(() => {
    const checkTokenExpiry = async () => {
      const token = localStorage.getItem(TOKEN_STORAGE_KEY);
      if (!token) return;

      const timeLeft = getTimeUntilExpiry(token);
      
      if (timeLeft <= 0) {
        // Token expired — try silent refresh before logging out
        console.log('Token expired, attempting silent refresh');
        const refreshed = await attemptSilentRefresh();
        if (!refreshed) {
          setError('Istunto on vanhentunut. Kirjaudu uudelleen.');
          logout();
        }
      } else if (timeLeft <= EXPIRY_BUFFER) {
        // Token expiring soon — try silent refresh proactively
        console.log(`Token expiring in ${Math.round(timeLeft / 60000)} min, refreshing`);
        const refreshed = await attemptSilentRefresh();
        if (!refreshed) {
          setNeedsRefresh(true); // Fall back to OneTap prompt
        }
      }
    };

    // Check immediately on mount
    checkTokenExpiry();

    // Set up interval
    refreshTimerRef.current = window.setInterval(checkTokenExpiry, TOKEN_CHECK_INTERVAL);

    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, [logout, attemptSilentRefresh]);

  /** On mount, try to restore session from localStorage */
  useEffect(() => {
    const restoreSession = async () => {
      const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
      if (!storedToken) {
        setLoading(false);
        return;
      }

      if (isTokenExpired(storedToken, 0)) {
        // Token expired — try silent refresh
        console.log('Stored token expired, attempting silent refresh');
        const refreshed = await attemptSilentRefresh();
        if (!refreshed) {
          localStorage.removeItem(TOKEN_STORAGE_KEY);
        }
        setLoading(false);
        return;
      }

      const authInfo = await validateToken(storedToken);
      if (authInfo) {
        setAuth(authInfo);
        const timeLeft = getTimeUntilExpiry(storedToken);
        console.log(`Restored session, token valid for ${Math.round(timeLeft / 60000)} min`);
      } else {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
      }
      setLoading(false);
    };
    restoreSession();
  }, [validateToken, attemptSilentRefresh]);

  // Refresh token when tab becomes visible again
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      const token = localStorage.getItem(TOKEN_STORAGE_KEY);
      if (!token) return;
      if (isTokenExpired(token, EXPIRY_BUFFER)) {
        attemptSilentRefresh();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [attemptSilentRefresh]);

  return (
    <AuthContext.Provider value={{ auth, loading, error, login, logout, needsRefresh }}>
      {children}
    </AuthContext.Provider>
  );
}
